import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase, isConfigured } from "../lib/supabase.js";
import { linkAtLoad, tidyUrl } from "../lib/authLink.js";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/* Holds the signed-in session and the matching profile row (name, role,
   owner flag). The profile is created by a database trigger when the
   account is created, so it always exists by the time we look. */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  /* A password reset link signs somebody in for one purpose only. Until they
     have chosen a new password the app must not let them past, or they never
     set one and are locked out again next time. */
  const [recovering, setRecovering] = useState(linkAtLoad.recovery);
  const [linkError, setLinkError] = useState(linkAtLoad.error);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) return setProfile(null);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, email, full_name, role, is_owner, must_set_password")
      .eq("id", userId)
      .maybeSingle();
    if (error) console.error("profile load failed", error);
    setProfile(data ?? null);
  }, []);

  useEffect(() => {
    if (!isConfigured) { setLoading(false); return; }

    let active = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session ?? null);
      await loadProfile(data.session?.user?.id);
      if (active) setLoading(false);
      /* Only now. getSession waits for the tokens in the address bar to be
         read, so tidying any earlier would throw the link away before it had
         been used. */
      tidyUrl();
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, next) => {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY") setRecovering(true);
      if (event === "SIGNED_OUT") setRecovering(false);
      setSession(next ?? null);
      await loadProfile(next?.user?.id);
    });

    return () => { active = false; sub.subscription.unsubscribe(); };
  }, [loadProfile]);

  const value = {
    session,
    profile,
    loading,
    recovering: recovering && Boolean(session),
    linkError,
    clearLinkError: () => setLinkError(""),
    finishRecovery: () => setRecovering(false),
    user: session?.user ?? null,
    isAdmin: profile?.role === "admin",
    isOwner: profile?.is_owner === true,
    /* A viewer sees everything on their contracts and changes nothing. The
       database refuses their writes either way; this is so the screens do not
       offer buttons that could only produce an error. */
    isViewer: profile?.role === "viewer",
    canEdit: Boolean(profile) && profile.role !== "viewer",
    signIn: (email, password) =>
      supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password }),
    signOut: () => supabase.auth.signOut(),
    resetPassword: (email) =>
      supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/`,
      }),
    /* A temporary password opens one door: the screen that asks for a real
       one. Until they walk through it they reach nothing. */
    mustSetPassword: profile?.must_set_password === true,
    /* Goes through the server rather than straight at Supabase, because the
       same call has to clear the flag, and a person who could clear their own
       could keep the temporary password and skip the screen. */
    savePassword: async (password) => {
      const { data } = await supabase.auth.getSession();
      const res = await fetch("/.netlify/functions/set-my-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data?.session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return { error: new Error(body.error || "That password was not accepted.") };
      return { error: null, warning: body.partial ? body.error : "" };
    },
    refreshProfile: () => loadProfile(session?.user?.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
